/**
 * @fileoverview One author's public pages: the thing that makes a personal site
 * feel like a site.
 *
 * A page's address names its author, so the reverse -- every page by that
 * author -- is an ordinary relay query and needs no index of ours, no crawl and
 * no cache. That is why this is cheap and why the global directory is not: a
 * directory has to fan out across a follow graph, and this does not fan out at
 * all.
 *
 * THIS ROUTE DELIBERATELY DOES NOT COLLAPSE ITS FAILURES, and that is the
 * opposite of `loadPage` next door. The reason the page loader flattens every
 * outcome into one is that a private page must be indistinguishable from one
 * that never existed. Nothing here is private: every event listed is a public
 * event, the query that finds them is one any stranger can run against the
 * relay directly, and an author having no pages is not a secret. So "this
 * author has published nothing" is an honest, distinct answer and is shown as
 * one. Importing the page loader's discipline here would cost a real answer to
 * buy no privacy.
 *
 * WHAT IS AND IS NOT LISTED. Kind 33301 gives this author's pages, every one of
 * them since 2026-09-16. Kind 30023 is asked as well, and gives their long-form
 * articles written in other clients plus any page published here before the
 * reversal: an author's index should show their writing, not only the part of
 * it that went through us. An encrypted private page lands in the 33301 answer
 * too, but its tags are ciphertext, so `parseIndexEvent` cannot read a `d`
 * tag out of it and returns null, and it is dropped without a row. That is the
 * correct outcome and it is a consequence of the parser rather than a filter
 * here: there is no branch in this file that knows what a private page is, so
 * there is no branch to get wrong later.
 *
 * It also means the count of an author's private pages is not displayed. It is
 * still derivable by anyone who queries the relay themselves -- that is a
 * property of an unauthenticated relay and not something this page creates --
 * but we do not put it on a screen.
 *
 * NO AVATAR IS FETCHED. A kind 0 profile names a picture URL on a host of the
 * author's choosing, and loading it would hand every reader's IP address to
 * that host on page load. The name and the description are enough to identify
 * whose index this is, and they arrive in the event we already have.
 */

import { parseIndexEvent, type PageFormat, type RawEvent } from './indexEvent';
import { NIP23_LONG_FORM_KIND, PAGE_INDEX_KIND } from './kinds';

import { fetchAll, relaysFor } from '@/services/nostr/relays';

/** kind 0, NIP-01 user metadata. */
const PROFILE_KIND = 0;

/**
 * How many events are asked for per kind.
 *
 * A cap rather than everything, because this renders one row per page into a
 * single list and an author with ten thousand pages would otherwise build a
 * DOM nobody asked for. It is a display limit, not a correctness one: the
 * newest are what a reader wants first, and paging is a later problem that the
 * relay's own `until` cursor already solves.
 */
const PER_KIND_LIMIT = 200;

/** A single row: enough to list a page, never enough to render one. */
export interface AuthorPage {
  address: { kind: number; pubkey: string; identifier: string };
  title: string;
  summary: string;
  format: PageFormat;
  /** Best available date, newest-first ordering key. */
  at: number;
}

export interface AuthorProfile {
  pubkey: string;
  /** `display_name`, then `name`, then null. Never a fabricated placeholder. */
  name: string | null;
  about: string | null;
}

export type AuthorIndexLoad =
  | { status: 'ready'; profile: AuthorProfile; pages: AuthorPage[] }
  /** The URL segment does not name an author at all. */
  | { status: 'not-an-author' };

/**
 * Load an author's public pages from an `npub` or `nprofile`.
 *
 * The hints in an `nprofile` are queried FIRST, for the same reason the page
 * loader prefers a page's own hints: an author whose pages live on a relay we
 * have never heard of must still have an index here, or the portability claim
 * is only true for people who chose us.
 */
export async function loadAuthorIndex(
  pubkey: string,
  hints: readonly string[] = []
): Promise<AuthorIndexLoad> {
  if (!/^[0-9a-f]{64}$/.test(pubkey)) return { status: 'not-an-author' };

  const relays = relaysFor(hints);

  // THREE FILTERS, ONE KIND EACH, and not one filter naming three kinds.
  // cloistr-relay refuses a filter it considers complex with a bare CLOSED and
  // no partial answer, which is indistinguishable from an empty relay -- so a
  // filter that is refused looks exactly like an author with no pages. Narrow
  // filters are how that failure mode is avoided rather than detected.
  const [profileEvents, longForm, blobBacked] = await Promise.all([
    fetchAll(relays, { kinds: [PROFILE_KIND], authors: [pubkey], limit: 1 }),
    fetchAll(relays, {
      kinds: [NIP23_LONG_FORM_KIND],
      authors: [pubkey],
      limit: PER_KIND_LIMIT,
    }),
    fetchAll(relays, { kinds: [PAGE_INDEX_KIND], authors: [pubkey], limit: PER_KIND_LIMIT }),
  ]);

  return {
    status: 'ready',
    profile: parseProfile(pubkey, newest(profileEvents)),
    pages: collectPages([...longForm, ...blobBacked]),
  };
}

function newest(events: readonly RawEvent[]): RawEvent | null {
  return events.reduce<RawEvent | null>(
    (best, e) => (!best || e.created_at > best.created_at ? e : best),
    null
  );
}

/**
 * Turn page events into rows, newest first.
 *
 * DEDUPLICATED BY COORDINATE, not by event id. These events are addressable and
 * therefore replaceable: two relays can legitimately hold two versions of the
 * same page, and listing both would show a reader the same page twice with
 * different titles. The newest `created_at` wins, which is the same rule the
 * single-page loader uses, so a row and the page it opens agree about which
 * version is current.
 */
function collectPages(events: readonly RawEvent[]): AuthorPage[] {
  const byCoordinate = new Map<string, { at: number; createdAt: number; page: AuthorPage }>();

  for (const event of events) {
    const index = parseIndexEvent(event);
    // Unparseable covers a malformed event and an encrypted private one alike.
    // Neither is a row, and nothing here needs to know which it was.
    if (!index) continue;

    const key = `${index.address.kind}:${index.address.identifier}`;
    const existing = byCoordinate.get(key);
    if (existing && existing.createdAt >= index.createdAt) continue;

    byCoordinate.set(key, {
      at: index.publishedAt ?? index.updatedAt ?? index.createdAt,
      createdAt: index.createdAt,
      page: {
        address: index.address,
        title: index.title,
        summary: index.summary,
        format: index.format,
        at: index.publishedAt ?? index.updatedAt ?? index.createdAt,
      },
    });
  }

  return [...byCoordinate.values()].sort((a, b) => b.at - a.at).map((entry) => entry.page);
}

/**
 * Read a kind 0 profile.
 *
 * NEVER THROWS AND NEVER TRUSTS. `.content` is a JSON string written by whoever
 * published it and handed over by a relay: it can be malformed, it can be an
 * array, and every field in it can be a number instead of a string. A profile
 * that cannot be read degrades to an unnamed author index, which still lists
 * every page correctly -- the name is decoration and the pages are the point.
 */
function parseProfile(pubkey: string, event: RawEvent | null): AuthorProfile {
  const empty: AuthorProfile = { pubkey, name: null, about: null };
  if (!event) return empty;

  let data: unknown;
  try {
    data = JSON.parse(event.content);
  } catch {
    return empty;
  }
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return empty;

  const record = data as Record<string, unknown>;
  return {
    pubkey,
    name: text(record.display_name) ?? text(record.name),
    about: text(record.about),
  };
}

/** A field is usable only if it is a non-empty string. Length-capped because a
 * profile is remote input and a megabyte "name" is a layout attack. */
function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.length > 200 ? `${trimmed.slice(0, 200)}…` : trimmed;
}
